import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import NotificationBell from './NotificationBell.jsx';
import { useQuery } from '@tanstack/react-query';
import { useUnreadCount, NOTIFICATIONS_KEY } from '../hooks/useNotifications.js';
import { api } from '../lib/api';
import { ping, ring } from '../lib/ring.js';
import DndPrompt from './DndPrompt.jsx';
import { hasSection } from '../lib/permissions.js';
import Logo from './Logo.jsx';
import { useBackDismiss } from '../lib/backStack.js';

/* ---------------------------------------------------------------------------
 * Admin shell
 *
 * A health-monitoring-dashboard chrome: a fixed left sidebar on tablet/desktop,
 * and the SAME sidebar as a slide-in drawer on phones (this also runs inside a
 * Capacitor webview, so every icon is hand-written inline SVG — no icon library
 * or font CDN is available offline).
 * ------------------------------------------------------------------------- */

/** Hand-written 20–24px stroke icons. Each receives the current text colour so
 *  it inherits the active/inactive pill treatment. */
const icons = {
  reviews: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3 2.7 5.5 6 .9-4.4 4.2 1 6-5.3-2.8-5.3 2.8 1-6L3.3 9.4l6-.9L12 3Z" />
    </svg>
  ),
  app: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4M7 8l2 2-2 2M12 12h4" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  catalogue: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 8 12 3 3 8l9 5 9-5Z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
    </svg>
  ),
  deliveries: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h11v9H3z" />
      <path d="M14 9h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </svg>
  ),
  live: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21Z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  ),
  customers: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 6M17.5 20a5.5 5.5 0 0 0-3-4.9" />
    </svg>
  ),
  coupons: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9a2 2 0 0 0 0 6v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2a2 2 0 0 1 0-6V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2Z" />
      <path d="M15 5v14" strokeDasharray="2 2" />
    </svg>
  ),
  stock: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 7.5 12 3l9 4.5-9 4.5-9-4.5Z" />
      <path d="M3 12l9 4.5L21 12M3 16.5 12 21l9-4.5" />
    </svg>
  ),
  expenses: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h12a2 2 0 0 1 2 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 0 1 2-2Z" />
      <path d="M8 9h6M8 13h4" />
    </svg>
  ),
  payouts: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M7 15h3" />
    </svg>
  ),
  reports: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 15l3.5-4 3 2.5L20 7" />
    </svg>
  ),
  announcements: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1Z" />
      <path d="M15 8a4 4 0 0 1 0 8" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </svg>
  ),
};

const NAV = [
  { section: 'dashboard', to: '/admin', label: 'Dashboard', icon: icons.dashboard, end: true },
  { section: 'catalogue', to: '/admin/products', label: 'Catalogue', icon: icons.catalogue },
  { section: 'deliveries', to: '/admin/deliveries', label: 'Deliveries', icon: icons.deliveries },
  { section: 'deliveries', to: '/admin/live', label: 'Live drivers', icon: icons.live },
  { section: 'coupons', to: '/admin/coupons', label: 'Coupons', icon: icons.coupons },
  { section: 'customers', to: '/admin/customers', label: 'Customers', icon: icons.customers },
  { section: 'customers', to: '/admin/reviews', label: 'Reviews', icon: icons.reviews },
  { section: 'stock', to: '/admin/stock', label: 'Stock', icon: icons.stock },
  { section: 'expenses', to: '/admin/expenses', label: 'Expenditures', icon: icons.expenses },
  { section: 'expenses', to: '/admin/payouts', label: 'Driver payouts', icon: icons.payouts },
  { section: 'reports', to: '/admin/reports', label: 'Reports', icon: icons.reports },
  { section: 'notifications', to: '/admin/notifications', label: 'Notifications', icon: icons.bell, badge: true },
  { section: 'announcements', to: '/admin/announcements', label: 'Announcements', icon: icons.announcements },
  { section: 'settings', to: '/admin/settings', label: 'Settings', icon: icons.settings },
  { section: 'app', to: '/admin/app', label: 'App admin', icon: icons.app },
];

/* The four places ops actually live, plus a way into everything else. On a
 * phone the sidebar is a drawer, and a drawer is a poor home for the screens
 * someone opens twenty times a shift — so those get thumb-level tabs and the
 * drawer keeps the long tail. */
const TAB_SECTIONS = [
  { section: 'dashboard', to: '/admin', label: 'Dashboard', icon: icons.dashboard, end: true },
  { section: 'deliveries', to: '/admin/deliveries', label: 'Deliveries', icon: icons.deliveries },
  { section: 'customers', to: '/admin/customers', label: 'Customers', icon: icons.customers },
  { section: 'notifications', to: '/admin/notifications', label: 'Alerts', icon: icons.bell, badge: true },
];

function AdminTabBar({ user, onMore }) {
  const unread = useUnreadCount();
  const tabs = TAB_SECTIONS.filter((tab) => hasSection(user, tab.section));

  // overflow-hidden, and every tab allowed to shrink: on a phone set to large
  // text each tab grows with the root font, and five of them used to push the
  // last ones clean off the screen.
  return (
    <nav
      aria-label="Main sections"
      className="pad-safe-bottom fixed inset-x-0 bottom-0 z-40 overflow-hidden border-t border-oat-dark/70 bg-surface/95 shadow-[0_-6px_20px_-8px_rgba(14,59,34,0.14)] backdrop-blur-md lg:hidden"
    >
      <ul className="flex items-stretch justify-around pt-1">
        {tabs.map((tab) => (
          <li key={tab.to} className="min-w-0 flex-1 basis-0">
            <NavLink
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                [
                  'flex min-h-touch w-full min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 py-1.5 text-2xs font-semibold transition-colors duration-150',
                  isActive ? 'text-heading' : 'text-ink-faint active:text-brand',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={[
                      'relative flex items-center justify-center rounded-full px-3 py-0.5 transition-colors duration-150',
                      isActive ? 'bg-leaf-100' : 'bg-transparent',
                    ].join(' ')}
                  >
                    {tab.icon}
                    {tab.badge && unread > 0 && (
                      <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-persimmon px-1 text-[10px] font-bold tabular-nums text-white">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </span>
                  <span className="w-full truncate text-center">{tab.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
        <li className="min-w-0 flex-1 basis-0">
          <button
            type="button"
            onClick={onMore}
            className="flex min-h-touch w-full min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-0.5 py-1.5 text-2xs font-semibold text-ink-faint transition-colors duration-150 active:text-brand"
          >
            <span className="flex items-center justify-center rounded-full px-3 py-0.5">
              <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </span>
            <span className="w-full truncate text-center">More</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

function navLinkClass({ isActive }) {
  return [
    'flex min-h-touch items-center gap-3 rounded-2xl px-3.5 text-sm font-semibold transition-colors',
    isActive
      ? 'bg-leaf-800 text-milk shadow-button'
      : 'text-ink-muted hover:bg-leaf-100 hover:text-heading',
  ].join(' ');
}

/** The sidebar body, shared verbatim by the fixed desktop rail and the mobile
 *  drawer, so the two never drift apart. */
function SidebarContent({ onNavigate, logout, user }) {
  const unread = useUnreadCount();
  return (
    <div className="flex h-full flex-col">
      <div className="px-4 py-5">
        <Link to="/" onClick={onNavigate} className="flex min-h-touch items-center" aria-label="HealthyFruitsBox home">
          <Logo />
        </Link>
      </div>

      <nav className="flex-1 space-y-1 px-3" aria-label="Admin sections">
        {NAV.filter((item) => hasSection(user, item.section)).map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} className={navLinkClass}>
            {item.icon}
            <span className="flex-1">{item.label}</span>
            {item.badge && unread > 0 && (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-persimmon px-1.5 text-2xs font-bold tabular-nums text-white">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="space-y-1 border-t border-oat-dark/60 px-3 py-3">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex min-h-touch items-center gap-3 rounded-2xl px-3.5 text-sm font-semibold text-ink-muted transition-colors hover:bg-leaf-100 hover:text-heading"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span>Back to site</span>
        </Link>
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            logout();
          }}
          className="flex w-full min-h-touch items-center gap-3 rounded-2xl px-3.5 text-left text-sm font-semibold text-ink-muted transition-colors hover:bg-persimmon-light hover:text-persimmon-dark"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 12H3M8 7l-5 5 5 5" />
            <path d="M9 4h9a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H9" />
          </svg>
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Sound for the panel while it's open: the loud ring for a new order, and the
 * ordinary chime for everything else that arrives — a payout request, a
 * review, low stock. In a desktop browser no push arrives at all, and a bell
 * badge alone is easy to miss. Shares the bell's unread-count query, polled
 * faster here.
 */
function NewOrderRinger() {
  const { data } = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'unread'],
    queryFn: () => api.get('/api/me/notifications/unread-count'),
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });
  const ringKey = data?.ring?.key;
  const latest = data?.latest;
  useEffect(() => {
    if (ringKey) ring(`order:${ringKey}`);
  }, [ringKey]);
  useEffect(() => {
    // Anything that isn't the ringing alert gets the quiet chime instead.
    if (latest?.id && latest.type !== 'new_order') ping(`note:${latest.id}`);
  }, [latest?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function AdminShell({ children }) {
  const { logout, user } = useAuth();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Any navigation closes the drawer — otherwise it stays open over the new page.
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  // And so does the Android back button, before it navigates anywhere.
  useBackDismiss(drawerOpen, () => setDrawerOpen(false));

  return (
    <div className="flex min-h-[100dvh] bg-oat/40">
      <NewOrderRinger />
      {/* Fixed rail on tablet/desktop. */}
      <aside className="hidden w-60 shrink-0 border-r border-oat-dark/60 bg-surface lg:block">
        <div className="pad-safe-top sticky top-0 h-[100dvh] overflow-y-auto">
          <SidebarContent logout={logout} user={user} />
        </div>
      </aside>

      {/* Slide-in drawer + scrim on phones. */}
      <div className={drawerOpen ? 'lg:hidden' : 'pointer-events-none lg:hidden'}>
        <div
          className={[
            'fixed inset-0 z-40 bg-ink/40 transition-opacity',
            drawerOpen ? 'opacity-100' : 'opacity-0',
          ].join(' ')}
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
        <div
          className={[
            'pad-safe-top fixed inset-y-0 left-0 z-50 w-72 max-w-[80%] overflow-y-auto border-r border-oat-dark/60 bg-surface shadow-lift transition-transform',
            drawerOpen ? 'translate-x-0' : '-translate-x-full',
          ].join(' ')}
          role="dialog"
          aria-modal="true"
          aria-label="Admin navigation"
        >
          <SidebarContent onNavigate={() => setDrawerOpen(false)} logout={logout} user={user} />
        </div>
      </div>

      {/* Main column. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Slim mobile top bar. */}
        {/* The menu button and the bell are a touch target each, and those
            grow with the phone's text size; the logo between them is what
            gives way, rather than the bar running off the screen. */}
        <header className="pad-safe-top sticky top-0 z-30 flex items-center gap-2 border-b border-oat-dark/60 bg-surface/85 px-2 backdrop-blur-md sm:gap-3 sm:px-3 lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex min-h-touch min-w-touch items-center justify-center rounded-2xl text-heading transition-colors hover:bg-leaf-100"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <Link to="/admin" className="flex min-h-touch min-w-0 flex-1 items-center overflow-hidden" aria-label="Admin dashboard">
            <Logo />
          </Link>
          <NotificationBell to="/admin/notifications" className="shrink-0" />
        </header>

        {/* Bottom padding on phones clears the tab bar; the desktop rail needs
            none, so it is dropped from lg up. */}
        <main className="pad-safe-bottom min-w-0 flex-1 pb-24 lg:pb-0">
          <div className="px-4 pt-4 sm:px-6 [&:empty]:hidden">
            <DndPrompt />
          </div>
          {children}
        </main>
      </div>

      <AdminTabBar user={user} onMore={() => setDrawerOpen(true)} />
    </div>
  );
}
